import React, { useState, useEffect } from "react";
import styled from "styled-components";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import AOS from "aos";
import { useHistory } from "react-router-dom";
import { frontEnd } from "../../config/links";

const Subscriptions: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"GSSE" | "BPS" | "RACGP">("GSSE");
  const history = useHistory();

  useEffect(() => {
    // Initialize AOS with settings to only animate once
    AOS.init({
      once: true, // This is the key setting - animations will only happen once
      mirror: false, // Don't mirror animations when scrolling back up
      duration: 800,
      offset: 100, // Controls when the animation triggers
      easing: "ease-out"
    });

    // Refresh AOS on component mount
    AOS.refresh();
  }, []);

  const tabContent = {
    GSSE: {
      features: [
        "Thousands of new and past exam-style questions",
        "Detailed explanations with references to prescribed textbooks",
        "Automatically marked anatomy spot questions",
        "Full exam mode to simulate real test conditions",
        "Spaced repetition to reinforce learning and improve retention",
        "Smart progress tracking to help you focus your revision",
        "Mobile-friendly interface for flexible, on-the-go study"
      ],
      originalPrice: 120,
      currentPrice: 90,
      isFree: false
    },
    BPS: {
      features: [
        "Early access to the only dedicated BPS preparation platform, shaped by feedback from real users",
        "Hundreds of high-quality, exam-style questions tailored to the official BPS syllabus",
        "Hundreds of high-quality questions based on Robbins & Cotran ",
        "Detailed explanations and references to help you learn, not just memorise",
        "Realistic exam-style formatting to mirror the official BPS exam",
        "Smart progress tracking to help you focus your revision",
        "Spaced repetition built in to strengthen long-term retention",
        "Mobile-friendly design for seamless studying anytime, anywhere"
      ],
      originalPrice: 150,
      currentPrice: 130,
      isFree: false
    },
    RACGP: {
      features: [
        "Currently in beta testing — get early access to the only platform available, shaped by feedback from real users",
        "Completely free for premium access during the beta phase",
        "Hundreds of high-quality questions based on Robbins & Cotran ",
        "Detailed explanations and references to help you learn, not just memorise",
        "Realistic exam-style formatting to mirror the official BPS exam",
        "Smart progress tracking to help you focus your revision",
        "Spaced repetition built in to strengthen long-term retention",
        "Mobile-friendly design for seamless studying anytime, anywhere"
      ],
      originalPrice: 120,
      currentPrice: 90,
      isFree: true
    }
  };

  const currentContent = tabContent[activeTab];

  const handleGoToPrices = () => {
    if (currentContent.isFree) {
      // For free tabs (BPS/RACGP), navigate to register page
      history.push(frontEnd.register);
    } else {
      // For paid tabs (GSSE/BPS), navigate to exam-specific pricing
      const examPath =
        activeTab === "GSSE"
          ? "/gsse"
          : activeTab === "BPS"
          ? "/bps"
          : "/racgp";
      history.push({
        pathname: examPath,
        hash: "#subscription-plans",
        state: { scrollToSection: true }
      });
    }
  };

  return (
    <SubscriptionsContainer>
      <Title data-aos="fade-up" data-aos-duration="800">
        Pricing
      </Title>

      <TabContainer
        data-aos="fade-up"
        data-aos-duration="800"
        data-aos-delay="100"
      >
        <Tab active={activeTab === "GSSE"} onClick={() => setActiveTab("GSSE")}>
          GSSE
        </Tab>
        <Tab active={activeTab === "BPS"} onClick={() => setActiveTab("BPS")}>
          BPS
        </Tab>
        <Tab
          active={activeTab === "RACGP"}
          onClick={() => setActiveTab("RACGP")}
        >
          RACGP
        </Tab>
      </TabContainer>

      <ContentContainer>
        <FeaturesContainer
          data-aos="fade-right"
          data-aos-duration="1000"
          data-aos-delay="200"
        >
          <SubTitle>
            <span style={{ fontWeight: "bold" }}>{activeTab}</span> full
            preparation toolkit access includes:
          </SubTitle>
          <FeaturesList>
            {currentContent.features.map((feature, index) => (
              <FeatureItem
                key={index}
                data-aos="fade-up"
                data-aos-duration="600"
                data-aos-delay={200 + index * 100}
              >
                <CheckCircleIcon
                  style={{
                    color: "#8b5cf6",
                    width: "22px",
                    height: "22px",
                    marginRight: "10px"
                  }}
                />
                {feature}
              </FeatureItem>
            ))}
          </FeaturesList>
        </FeaturesContainer>
        <PricingVectorImage
          src="/assets/home/pricing-vector.png"
          alt="pricing-vector"
          data-aos="zoom-in"
          data-aos-duration="800"
        />
        <PricingCard
          data-aos="fade-left"
          data-aos-duration="1000"
          data-aos-delay="300"
        >
          {currentContent.isFree ? (
            <>
              <FreeText>
                <div className="free-banner">FREE</div>
                <div className="beta-text">Beta Access Available Now</div>
              </FreeText>
              <PurpleButton onClick={handleGoToPrices}>Try Now</PurpleButton>
              <div style={{ fontSize: "16px" }}>
                <span style={{ fontWeight: "bold" }}>Full premium access</span>{" "}
                during beta
              </div>
            </>
          ) : (
            <>
              <PriceText>
                <div className="original-price">
                  ${currentContent.originalPrice}
                </div>
                <div className="price-starts-at">
                  starts at{" "}
                  <span className="current-price">
                    ${currentContent.currentPrice}
                    <PriceVector
                      src="/assets/home/price-vector1.png"
                      alt="pricing-vector1"
                    />
                  </span>
                </div>
              </PriceText>
              <PurpleButton onClick={handleGoToPrices}>
                Go to {activeTab} Prices
              </PurpleButton>
              <div style={{ fontSize: "16px" }}>
                <span style={{ fontWeight: "bold" }}>Free limited</span> access
                available
              </div>
            </>
          )}
        </PricingCard>
      </ContentContainer>
    </SubscriptionsContainer>
  );
};

const SubscriptionsContainer = styled.div`
  padding: 64px;
  text-align: center;
  background-image: url("/assets/home/home-hero-bg.webp");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: -1;
  min-height: 792px;

  @media (max-width: 768px) {
    padding: 32px 0px;
    min-height: auto;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1c1c1f;
  margin-bottom: 32px;
  @media (max-width: 768px) {
    margin-bottom: 16px;
    font-size: 24px;
  }
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 32px;
  background: #f3f4f6;
  width: fit-content;
  margin: 0 auto;
  padding: 4px;
  border-radius: 25px;
  margin-bottom: 56px;
  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 8px 24px;
  border-radius: 20px;
  border: none;
  background-color: ${props => (props.active ? "#000" : "transparent")};
  color: ${props => (props.active ? "#fff" : "#000")};
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
  font-size: 16px;

  &:hover {
    background-color: ${props => (props.active ? "#000" : "#E5E7EB")};
  }
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 64px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 32px;
    padding: 0 16px;
  }
`;

const FeaturesContainer = styled.div`
  width: 443px;
  order: 1;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 443px;
    order: 2;
  }
`;

const FeaturesList = styled.ul`
  list-style: none;
  text-align: left;
  padding: 0;
  margin-bottom: 32px;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  font-size: 16px;
  color: #1c1c1f;
`;

const PricingCard = styled.div`
  background: white;
  padding: 36px 79px 60px 79px;
  border-radius: 24px;
  box-shadow: 0px 4px 25px 0px rgba(0, 0, 0, 0.05);
  width: 443px;
  height: 295px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  order: 3;
  margin: auto 0;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 443px;
    padding: 0px 30px 10px 30px;
    order: 1;
  }
`;

const PriceText = styled.div`
  text-align: center;

  .original-price {
    color: #6b7280;
    text-decoration: line-through;
    font-size: 16px;
    margin-left: auto;
    transform: translate(64px, 14px);
    @media (max-width: 768px) {
      transform: translate(42px, 7px);
    }
  }

  .current-price {
    font-size: 32px;
    color: #4628ff;
    font-weight: 600;
    height: 32px;
    position: relative;

    @media (max-width: 768px) {
      font-size: 21px;
    }

    @media (max-width: 1168px) {
      font-size: 24px;
    }
  }

  .price-starts-at {
    font-size: 32px;
    font-weight: bold;
    @media (max-width: 768px) {
      font-size: 21px;
    }

    @media (max-width: 1168px) {
      font-size: 24px;
    }
  }
`;

const PriceVector = styled.img.attrs({ width: 66, height: 8 })`
  position: absolute;
  bottom: -5px;
  right: 0;
`;

const PurpleButton = styled.button`
  background-color: #4628ff;
  color: white;
  border: none;
  padding: 16px 16px;
  border-radius: 16px;
  cursor: pointer;
  font-weight: 500;
  font-size: 16px;
  width: 100%;
  max-width: 178px;

  &:hover {
    background-color: #7c3aed;
  }

  @media (max-width: 1168px) {
    max-width: 200px;
    width: 100%;
  }
`;

const SubTitle = styled.h2`
  font-size: 37px;
  font-weight: 600;
  color: #1c1c1f;
  margin-bottom: 16px;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 16px;
    text-align: left;
  }
`;

const PricingVectorImage = styled.img.attrs({ width: 209, height: 111 })`
  margin: auto 0;
  order: 2;

  @media (max-width: 1068px) {
    display: none;
  }
`;

const FreeText = styled.div`
  text-align: center;

  .free-banner {
    font-size: 48px;
    color: #10b981;
    font-weight: 700;
    margin-bottom: 8px;

    @media (max-width: 768px) {
      font-size: 36px;
    }
  }

  .beta-text {
    font-size: 18px;
    font-weight: 500;
    color: #1c1c1f;

    @media (max-width: 768px) {
      font-size: 16px;
    }
  }
`;

export default Subscriptions;
